import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Package, AlertTriangle, LayoutDashboard } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const displayName = user?.name || user?.raw?.nombre || 'Cliente';

  const menuItems = [
    {
      title: 'Recepción de Ropa',
      description: 'Consulta el historial de recepciones de ropa sucia',
      icon: <Truck className="h-10 w-10 text-primary" />,
      path: '/cliente/recepcion',
      color: 'bg-primary/10',
      isReady: true,
    },
    {
      title: 'Inventario',
      description: 'Revisa el inventario actual de prendas',
      icon: <Package className="h-10 w-10 text-emerald-500" />,
      path: null,
      color: 'bg-emerald-500/10',
      isReady: false,
    },
    {
      title: 'PQRS',
      description: 'Peticiones, quejas, reclamos y sugerencias',
      icon: <AlertTriangle className="h-10 w-10 text-amber-500" />,
      path: null,
      color: 'bg-amber-500/10',
      isReady: false,
    },
  ];

  const handleMenuClick = (item) => {
    if (!item.isReady) {
      toast({
        title: 'En desarrollo',
        description: 'Esta funcionalidad estará disponible próximamente',
      });
      return;
    }
    navigate(item.path);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 md:p-6 lg:p-8">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <LayoutDashboard className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Portal de Clientes</CardTitle>
              <CardDescription>Bienvenido, {displayName}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {menuItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleMenuClick(item)}
                className={`${item.color} p-6 rounded-lg border border-border shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 ${!item.isReady ? 'opacity-70' : ''}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`p-3 rounded-full ${item.color}`}>
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold mt-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  {!item.isReady && (
                    <span className="mt-2 px-2 py-1 text-xs rounded bg-muted text-muted-foreground">
                      Próximamente
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ClientDashboard;
